import * as React from 'react';
import { getLocalization } from 'global/global';
import { Button, Col, Container } from 'react-bootstrap';
import { CHART_DATA_FILTER, ChartDataPoints, ChartModel } from 'Interfaces/ChartInterface';
import { useAppDispatch, useAppSelector } from 'index';
import { uniqueFormsSelector } from 'reducers/formsReducer';
import { FormInterface } from 'Interfaces/Forms/FormsInterface';
import { QuestionInterface } from 'Interfaces/Forms/QuestionInterface';
import FormUtils from 'views/SingleInstance/utils/FormUtils';
import { getFormUtils } from 'views/SingleInstance/utils/FormUtilsHolder';
import { addNewChart, saveCharts, setChartGuidedSetup } from 'actions/chartsActions';
import { toggleSelectedModule } from 'actions/moduleSelectionActions';
import { MODULES } from 'Interfaces/ModuleSelectionInterface';
import { fetchPOI } from 'actions/pois';
import { setChartDataPoints as setChartDataPointsAction } from 'actions/chartsActions';
import { QueryFiltersInterface } from 'reducers/filtersMenuReducer';
import { getChartFilters, getChartName, getChartType, isNumericQuestion, isSelectQuestion } from '../utils';
import { processChartData } from '../utils/ChartData';
import FormsSelection from './FormSelection';
import ChartSelection from './ChartSelection';
import ChartComponent from './ChartComponent';


export default function GuidedChart() {
  const forms: FormInterface[] = useAppSelector(state => uniqueFormsSelector(state));
  const filtersMenu = useAppSelector(state => state.filtersMenu);
  const locationLabels = useAppSelector(state => state.clientPersist.locationLabels);
  const locations = useAppSelector(state => state.locations.collection);
  const users = useAppSelector(state => state.users);
  const dispatch = useAppDispatch();
  const [form, setForm] = React.useState<string | null>(null);
  const [validateForm, setValidateForm] = React.useState(false);
  const [validate, setValidate] = React.useState(false);
  const formUtils = React.useRef<FormUtils | null>(null);

  const [step, setStep] = React.useState<'FORM' | 'CHART'>('FORM');
  const [questions, setQuestions] = React.useState<string[]>([]);
  const [xAxis, setXAxis] = React.useState<string[]>([]);
  const [chartType, setChartType] = React.useState('');
  const [useFilters, setUserFilters] = React.useState(false);
  const [combineCharts, setCombineCharts] = React.useState(false);
  const [combineAndStack, setCombineAndStack] = React.useState(false);
  const [stacking, setStacking] = React.useState('');
  const [radarDataPoints, setRadarDataPoints] = React.useState<string[]>([]);
  const [dataFilter, setDataFilter] = React.useState<CHART_DATA_FILTER>('OWN');
  const [charts, setCharts] = React.useState<ChartModel[]>([]);
  const [chartDataPoints, setChartDataPoints] = React.useState<ChartDataPoints>({});

  React.useEffect(() => {
    if (chartType && chartType === 'BAR') {
      setQuestions(['countQuestion']);
    }
  }, [chartType]);

  const updateModel = (model: ChartModel, index: number) => {
    setCharts(charts.map((c, i) => {
      if (i === index) {
        return {...c, ...model };
      } else {
        return c;
      }
    }));
  };

  const reset = () => {
    setQuestions([]);
    setRadarDataPoints([]);
    setXAxis([]);
  };

  const getBody = () => {
    if (step === 'FORM') {
      return (
        <FormsSelection
          selectedForm={form}
          validate={validateForm}
          setSelectedForm={(id) => setForm(id)}
        />
      );
    } else {
      return form ? (
        <ChartSelection
          selectedForm={form}
          questions={questions}
          xAxis={xAxis}
          chartType={chartType}
          radarDataPoints={radarDataPoints}
          dataFilter={dataFilter}
          validate={validate}
          useFilters={useFilters}
          combineCharts={combineCharts}
          combineAndStack={combineAndStack}
          onDataFilterSelected={setDataFilter}
          onDataPointSelected={(dp) => {
            setRadarDataPoints(dp);
            setValidate(false);
          }}
          setSelectedQuestion={onQuestionSelect}
          setXAxis={onXAxisQuestionSelect}
          setChartType={(type) => {
            setChartType(type);
            setValidate(false);
            setStacking('');
          }}
          stacking={stacking}
          setStacking={(s) => setStacking(s)}
          onUseFiltersChange={(checked) => setUserFilters(checked)}
          onCombineChartsChange={(checked) => setCombineCharts(checked)}
          onCombineAndStackChange={(checked) => setCombineAndStack(checked)}
          reset={reset}
        />
      ) : <div />;
    }
  };

  const onQuestionSelect = (id: string, checked: boolean) => {
    if (checked) {
      setQuestions(questions.concat([id]));
    } else {
      setQuestions(questions.filter(q => q !== id));
    }
    setValidate(false);
  };

  const onXAxisQuestionSelect = (id: string, checked: boolean) => {
    if (checked) {
      setXAxis(xAxis.concat([id]));
    } else {
      setXAxis(xAxis.filter(q => q !== id));
    }
    setValidate(false);
  };

  const moveToNext = () => {
    if (step === 'FORM') {
      if (form) {
        setStep('CHART');
        setValidateForm(false);
      } else {
        setValidateForm(true);
      }
    } else {
      defineChart();
    }
  };

  const moveBack = () => {
    if (step === 'FORM') {
      dispatch(toggleSelectedModule(MODULES.FEED));
      dispatch(setChartGuidedSetup(false));
    } else {
      setStep('FORM');
    }
  };

  const createFormUtils = () => {
    const model = forms.find(f => f.ref === form);
    if (model) {
      formUtils.current = getFormUtils(model);
    }
  };

  const getFilters = () => {
    const locations: string[] = [];
    const levels: string[] = [];
    filtersMenu.selectedLocations.forEach(location => {
      locations.push(`${location.key}`);
      levels.push(`${Number(location.level) + 1}`);
    });
    return {
      locations: locations.join(','),
      locationLevels: levels.join(','),
      fromDate: filtersMenu.selectedDates.from || '',
      toDate: filtersMenu.selectedDates.to || '',
      users: filtersMenu.selectedUsers.map(u => u.id).join(','),
      dateFilterBy: filtersMenu.selectedDates.filterBy,
    };
  };

  const getXAxisTitle = (x: string, xQn: QuestionInterface) => {
    if (x.includes('location')) {
      const index = x.substring(x.indexOf('location') + 8);
      return locationLabels[Number(index) - 1];
    } else if (x.includes('when')) {
      const prefix = x.substring(0, x.indexOf('-'));
      const suffix = x.substring(x.lastIndexOf('-') + 1);
      return getLocalization(`${prefix}${suffix.charAt(0).toUpperCase()}${suffix.substring(1)}`);
    }
    switch (x) {
      case 'createdBy':
        return getLocalization('createdBy');
      case 'user_id':
        return getLocalization('editor');
      case 'created':
        return getLocalization('createdOn');
      case 'modified':
        return getLocalization('lastUpdatedAt');
    }
    if (xQn) {
      return xQn.text;
    }
    return '';
  };

  /* eslint-disable complexity */
  const defineCharts = () => {
    createFormUtils();
    if (!formUtils.current || !form) {
      return;
    }
    const chartModels: ChartModel[] = [];
    const filters = useFilters ? getFilters() : {};
    if (chartType === 'RADAR') {
      const model: ChartModel = {
        type: 'RADAR',
        id: `chart-radar-${Date.now()}`,
        name: getChartName(formUtils.current.getModel().name, 'RADAR'),
        questions: [],
        average: false,
        scale: 'DAY',
        forms: {
          [form]: {
            questions: questions,
            formId: form
          }
        },
        dataPoints: radarDataPoints,
        dataFilter,
      };
      chartModels.push(model);
    } else {
      if (combineCharts && chartType === 'LINE') {
        const numeric = questions.filter(q => {
          const qn = formUtils.current?.getQuestion(q);
          return qn && isNumericQuestion(qn.type, qn.numericOnly);
        });
        if (numeric.length) {
          const model: ChartModel = {
            type: 'LINE_NUMERIC',
            id: `new-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
            name: getChartName(formUtils.current.getModel().name, 'LINE_NUMERIC'),
            questions: [],
            average: false,
            scale: 'DAY',
            combine: 'FIELD',
            forms: {
              [form]: {
                questions: numeric,
                formId: form
              }
            },
            dataPoints: [],
            dataFilter: 'OWN',
            ...filters
          };
          for (const x of xAxis) {
            const xQn = formUtils.current.getQuestion(x);
            const title = getXAxisTitle(x, xQn);
            const newModel = {
              ...model,
              xaxis: x,
              name: `Combined Numeric ${model.name} per ${title}`,
              id: `chart-combined-${numeric.join('-')}-${x}-${Date.now()}`
            };
            chartModels.push(newModel);
          }
        }
        const select = questions.filter(q => {
          const qn = formUtils.current?.getQuestion(q);
          return qn && isSelectQuestion(qn.type);
        });
        if (select.length) {
          for (const q of select) {
            const qn = formUtils.current?.getQuestion(q);
            const model: ChartModel = {
              type: 'LINE_CHOICE',
              id: `new-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
              name: getChartName(formUtils.current.getModel().name, 'LINE_CHOICE'),
              questions: [],
              average: false,
              scale: 'DAY',
              forms: {
                [form]: {
                  questions: [q],
                  formId: form
                }
              },
              dataPoints: [],
              dataFilter: 'OWN',
              ...filters
            };
            for (const x of xAxis) {
              const xQn = formUtils.current.getQuestion(x);
              const title = getXAxisTitle(x, xQn);
              const newModel = {
                ...model,
                xaxis: x,
                name: `${model.name} ${qn.text}  per ${title}`,
                id: `chart-combined-${select.join('-')}-${x}-${Date.now()}-${Math.floor(Math.random() * 100000)}`
              };
              chartModels.push(newModel);
            }
          }
        }
      } else {
        if (questions.length === 0 && xAxis.length) {
          for (const q of xAxis) {
            const qn = formUtils.current.getQuestion(q);
            const count = formUtils.current.getQuestion('countQuestion');
            const type = getChartType(chartType, count);
            if (type !== null) {
              const title = getXAxisTitle(q, qn);
              const model: ChartModel = {
                type: type,
                id: `new-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
                name: `${getChartName(formUtils.current.getModel().name, type)} - ${title}`,
                questions: [],
                average: false,
                scale: 'DAY',
                stack: stacking,
                forms: {
                  [form]: {
                    questions: ['countQuestion'],
                    formId: form
                  }
                },
                dataPoints: [],
                xaxis: q,
                dataFilter: 'OWN',
                ...filters
              };
              chartModels.push(model);
            }
          }
        } else {
          if ((combineCharts || combineAndStack) && chartType === 'BAR') {
            for (const q of xAxis) {
              const qn = formUtils.current.getQuestion(q);
              const count = formUtils.current.getQuestion('countQuestion');
              const type = getChartType(chartType, count);
              if (type !== null) {
                const title = getXAxisTitle(q, qn);
                const model: ChartModel = {
                  type: type,
                  id: `new-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
                  name: `${getChartName(formUtils.current.getModel().name, type)} - ${title}`,
                  questions: [],
                  average: false,
                  scale: 'DAY',
                  stack: combineAndStack ? stacking || 'FIELD' : stacking,
                  combine: 'FIELD',
                  forms: {
                    [form]: {
                      questions,
                      formId: form
                    }
                  },
                  dataPoints: [],
                  xaxis: q,
                  dataFilter: 'OWN',
                  ...filters
                };
                chartModels.push(model);
              }
            }
          } else {
            for (const q of questions) {
              const qn = formUtils.current.getQuestion(q);
              const type = getChartType(chartType, qn);
              if (type) {
                const model: ChartModel = {
                  type: type,
                  id: `new-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
                  name: getChartName(formUtils.current.getModel().name, type),
                  questions: [],
                  average: false,
                  scale: 'DAY',
                  stack: stacking,
                  forms: {
                    [form]: {
                      questions: [q],
                      formId: form
                    }
                  },
                  dataPoints: [],
                  dataFilter: 'OWN',
                  ...filters
                };
                if (chartType === 'BAR' || chartType === 'LINE') {
                  for (const x of xAxis) {
                    const xQn = formUtils.current.getQuestion(x);
                    const title = getXAxisTitle(x, xQn);
                    const newModel = {
                      ...model,
                      xaxis: x,
                      name: `${model.name} ${qn.text} per ${title}`,
                      id: `chart-${q}-${x}-${Date.now()}-${Math.floor(Math.random() * 100000)}`
                    };
                    chartModels.push(newModel);
                  }
                } else if (chartType === 'PIE') {
                  const xQn = formUtils.current.getQuestion(q);
                  const title = getXAxisTitle(q, xQn);
                  chartModels.push({...model, name: `${model.name} ${title}`, id: `chart-${q}-${Date.now()}` });
                }
              }
            }
          }
        }
      }
    }
    setCharts(chartModels);
    if (chartType !== 'RADAR') {
      void getChartDataPoints(chartModels);
    }
  };

  const defineChart = () => {
    if (!chartType || (chartType === 'BAR' && xAxis.length === 0) || (chartType !== 'BAR' && questions.length === 0)) {
      setValidate(true);
      return;
    } else if (chartType === 'RADAR' && questions.length < 3) {
      setValidate(true);
      return;
    } else if ((chartType === 'LINE') && xAxis.length === 0) {
      setValidate(true);
      return;
    }
    defineCharts();
  };

  const onCancel = () => {
    reset();
  };

  const onSave = async () => {
    const response = await dispatch(saveCharts(charts.map(c => ({...c, id: ''}))));
    for (const chart of response) {
      dispatch(addNewChart(chart));
    }
    dispatch(setChartGuidedSetup(false));
  };

  const getChartDataPoints = async (chartModels: ChartModel[]) => {
    if (formUtils.current) {
      const fields = [...questions];
      xAxis.forEach(x => {
        if (x.indexOf('when-updated') > -1) {
          fields.push('modified');
        } else if (x.indexOf('when-created') > -1) {
          fields.push('created');
        } else {
          fields.push(x);
        }
      });
      let allFilters: QueryFiltersInterface[] = [];
      let _query = {};
      if (useFilters) {
        const _filters = getFilters();
        const { query, filters } = getChartFilters(_filters);
        allFilters = filters;
        _query = query;
      }
      const response = await dispatch(fetchPOI(
        formUtils.current.getModel().ref, undefined, undefined, allFilters, _query,
        ['Name', 'questionnaire_id'].concat(fields).join(',')
      ));
      const json = await response.json();
      if (Array.isArray(json)) {
        const chartData = {};
        for (const chart of chartModels) {
          chartData[chart.id] = processChartData(chart, json, formUtils.current.getModel(), locations, users);
          dispatch(setChartDataPointsAction(chart.id, chartData[chart.id]));
        }
        setChartDataPoints(chartData);
      }
    }
  };

  const ChartCreator = React.useCallback(() => {
    return (
      <>
        {getBody()}
        <Container className="d-flex justify-content-between align-items-start">
          <Button size="sm" onClick={moveBack}>
            {getLocalization(step === 'FORM' ? 'cancel' : 'back')}
          </Button>
          <Button size="sm" onClick={moveToNext}>
            {getLocalization(step === 'FORM' ? 'next' : 'defineReport')}
          </Button>
        </Container>
      </>
    );
  }, [ form, xAxis, questions, chartType, radarDataPoints, dataFilter,
    step, validateForm, validate, useFilters, combineCharts, stacking, combineAndStack]);

  const size = charts.length ? 4 : undefined;
  const className = charts.length ? 'row' : 'container';
  return (
    <div className={`pb-3 ${className}`}>
      <Col sm={size} md={size}>
        {ChartCreator()}
      </Col>
      <Col>
        {charts.length > 0 ? (
          <Container className="d-flex justify-content-between align-items-start mb-3">
            <Button size="sm" variant="secondary" onClick={onCancel}>
              {getLocalization('cancel')}
            </Button>
            <Button size="sm" variant="success" onClick={() => void onSave()}>
              {getLocalization('save')}
            </Button>
          </Container>
        ) : null}
        {charts.map((chart, index) => (
          <ChartComponent
            key={`chart-panel-${chart.id}`}
            model={chart}
            chartDataPoints={{[`${form}`]: chartDataPoints[chart.id]}}
            onModelChange={(model) => updateModel(model, index)}
          />
        ))}
      </Col>
    </div>
  );
}
